import { COMBAT_LIMITS, COMBAT_THEATER, combatActorGeometry, combatWeaponOrigin, isCombatEventList, isCombatSnapshot,
  type CombatEvent, type CombatSnapshot, type PackedCombatActor, type PackedCombatRocket } from "@endclock/shared";
import type { ActorContactShadows } from "../engine/actor-contact-shadows.js";
import type { AtmosphereQuality } from "../engine/atmosphere.js";
import type { Camera } from "../engine/camera.js";
import type { LocalLight } from "../engine/lighting.js";
import type { GpuMesh, Renderer, StaticRenderMaterial } from "../engine/renderer.js";
import { CombatEffectRenderer, type RocketVisual } from "./effect-renderer.js";
import { COMBAT_VFX_BUDGET, CombatEffects } from "./effects.js";
import { MACHINE_PART_CAPACITY, MachineParts, machineBox, machineWheel, type MachinePose } from "./model.js";
import { CombatVehicleCrew } from "./vehicle-crew.js";
import { houndSensorPresentation } from "./model.js";
import { CombatAudio } from "./audio.js";

export const COMBAT_INTERPOLATION_DELAY_MS = 100;
export const COMBAT_EXTRAPOLATION_LIMIT_MS = 150;
const METAL: StaticRenderMaterial = { alphaCutoff: 0, doubleSided: false };
const GLOW: StaticRenderMaterial = { alphaCutoff: 0, doubleSided: false, emissiveColor: [1, .74, .48], emissiveStrength: .55 };
interface Actor {
  previous: PackedCombatActor; current: PackedCombatActor; previousAt: number; currentAt: number;
  readonly pose: MachinePose; readonly parts: MachineParts; visible: boolean; recoilAt: number; cannonAt: number; hitAt: number;
}
interface Rocket {
  previous: PackedCombatRocket; current: PackedCombatRocket; previousAt: number; currentAt: number;
  readonly pose: RocketVisual; trailAt: number;
}

/** A single client presentation owner. It cannot choose targets, hit results or health. */
export class CombatWorld {
  private readonly actors = new Map<number, Actor>();
  private readonly rockets = new Map<number, Rocket>();
  private readonly recentEvents = new Set<number>();
  private readonly eventOrder: number[] = [];
  private readonly destroyedRockets = new Map<number, number>();
  private readonly effects: CombatEffects;
  private readonly crew: CombatVehicleCrew;
  private readonly audio = new CombatAudio();
  private readonly effectRenderer: CombatEffectRenderer;
  private readonly box: GpuMesh;
  private readonly wheel: GpuMesh;
  private readonly metalData = new Float32Array(COMBAT_LIMITS.actors * MACHINE_PART_CAPACITY * 20);
  private readonly glowData = new Float32Array(COMBAT_LIMITS.actors * 12 * 20);
  private readonly wheelData = new Float32Array(COMBAT_LIMITS.actors * 16 * 20);
  private readonly rocketVisuals: RocketVisual[] = [];
  private readonly rocketLights: LocalLight[] = [];
  private epoch = -1; private tick = -1; private at = 0; private now = 0; private updatedAt = 0;
  private phase: CombatSnapshot["phase"] = "SKIRMISH";
  private enabled = false; private maximumEventId = -1;
  private quality: AtmosphereQuality = "medium";
  private actorDrawCalls = 0; private shadowDrawCalls = 0; private visibleCount = 0;
  private duplicateEvents = 0; private droppedEvents = 0; private animationCpuMs = 0; private effectsUpdateCpuMs = 0; private vfxRenderCpuMs = 0; private peakLights = 0;

  constructor(private readonly renderer: Renderer, private readonly heightAt: (x: number, z: number) => number) {
    this.effects = new CombatEffects(heightAt, (id, epoch, barrelIndex, targetId, targetEpoch) => {
      const actor = this.actors.get(id);
      if (!actor || actor.current[1] !== epoch || actor.current[13] === 5) return undefined;
      const target = targetId === undefined ? undefined : this.actors.get(targetId);
      if (targetId !== undefined && (!target || target.current[13] === 5 || targetEpoch !== undefined && target.current[1] !== targetEpoch)) return undefined;
      const [x, y, z] = combatWeaponOrigin({ type: actor.current[2], ...actor.pose }, "quad", barrelIndex);
      return { x, y, z };
    }); this.crew = new CombatVehicleCrew(renderer);
    this.box = renderer.createMesh(machineBox()); this.wheel = renderer.createMesh(machineWheel());
    this.effectRenderer = new CombatEffectRenderer(renderer);
  }

  ingest(snapshot: CombatSnapshot, at: number): void {
    if (!Number.isFinite(at) || !isCombatSnapshot(snapshot) || snapshot.epoch < this.epoch
      || snapshot.epoch === this.epoch && (snapshot.tick < this.tick || snapshot.tick === this.tick && at <= this.at)) return;
    if (snapshot.epoch > this.epoch) this.reset();
    this.epoch = snapshot.epoch; this.tick = snapshot.tick; this.at = at; this.enabled = snapshot.enabled; this.phase = snapshot.phase;
    if (!this.enabled) { this.actors.clear(); this.rockets.clear(); this.effects.reset(); this.audio.reset(); this.crew.reset(); return; }
    const actorIds = new Set<number>(), rocketIds = new Set<number>();
    for (const current of snapshot.actors) {
      actorIds.add(current[0]); const actor = this.actors.get(current[0]);
      if (actor && actor.current[1] === current[1]) {
        actor.previous = actor.current; actor.previousAt = actor.currentAt; actor.current = current; actor.currentAt = at;
      } else this.actors.set(current[0], { previous: current, current, previousAt: at, currentAt: at,
        pose: { x: current[4], y: current[5], z: current[6], yaw: current[7], turretYaw: current[8], aimPitch: current[9],
          speed: 0, phase: current[0] * 1.7, collapse: current[13] === 5 ? 1 : 0, recoil: 0, damaged: 1 - current[12],
          state: current[13], steering: current[15], damageStage: current[17], damageZone: current[18], damageSeed: current[19] },
        parts: new MachineParts(), visible: false, recoilAt: -Infinity, cannonAt: -Infinity, hitAt: -Infinity });
    }
    for (const id of this.actors.keys()) if (!actorIds.has(id)) this.actors.delete(id);
    this.crew.retain(actorIds);
    for (const current of snapshot.rockets) {
      if (this.destroyedRockets.has(current[0])) continue;
      rocketIds.add(current[0]); const rocket = this.rockets.get(current[0]);
      if (rocket && rocket.current[1] === current[1]) {
        rocket.previous = rocket.current; rocket.previousAt = rocket.currentAt; rocket.current = current; rocket.currentAt = at;
      } else this.rockets.set(current[0], { previous: current, current, previousAt: at, currentAt: at,
        pose: { x: current[4], y: current[5], z: current[6], vx: current[7], vy: current[8], vz: current[9] }, trailAt: -Infinity });
    }
    for (const id of this.rockets.keys()) if (!rocketIds.has(id)) this.rockets.delete(id);
    for (const [id, destroyedTick] of this.destroyedRockets) if (snapshot.tick - destroyedTick > 40) this.destroyedRockets.delete(id);
  }

  ingestEvents(events: readonly CombatEvent[], at: number): void {
    if (!this.enabled || !Number.isFinite(at) || !isCombatEventList(events)) return;
    for (const event of events) {
      if (event.epoch !== this.epoch || event.tick < this.tick - COMBAT_LIMITS.eventLifetimeTicks) { this.droppedEvents++; continue; }
      const eventActor = this.actors.get(event.actorId), eventTarget = event.targetId === undefined ? undefined : this.actors.get(event.targetId);
      // A projectile already in flight can outlive its owner; its world impact remains authoritative.
      const independentImpact = event.projectileId !== undefined && (event.kind === "impact" || event.kind === "explosion");
      if (!independentImpact && (eventActor && eventActor.current[1] !== event.actorEpoch
        || eventTarget && event.targetEpoch !== undefined && eventTarget.current[1] !== event.targetEpoch)) { this.droppedEvents++; continue; }
      if (this.recentEvents.has(event.id) || event.id <= this.maximumEventId - COMBAT_LIMITS.recentEvents) { this.duplicateEvents++; continue; }
      this.maximumEventId = Math.max(this.maximumEventId, event.id);
      this.recentEvents.add(event.id); this.eventOrder.push(event.id);
      if (this.eventOrder.length > COMBAT_LIMITS.recentEvents) this.recentEvents.delete(this.eventOrder.shift()!);
      const actor = this.actors.get(event.actorId);
      if (actor && actor.current[1] === event.actorEpoch && (event.kind === "gun" || event.kind === "rocket_launch" || event.kind === "hound_gun" || event.kind === "quad_burst")) actor.recoilAt = at;
      if (actor && actor.current[1] === event.actorEpoch && event.kind === "cannon") actor.cannonAt = at;
      if (eventTarget && eventTarget.current[1] === event.targetEpoch && (event.kind === "impact" || event.kind === "explosion")) eventTarget.hitAt = at;
      if ((event.kind === "explosion" || event.kind === "impact") && event.projectileId !== undefined) {
        this.rockets.delete(event.projectileId); this.destroyedRockets.set(event.projectileId, event.tick);
        if (this.destroyedRockets.size > COMBAT_LIMITS.rockets * 2) this.destroyedRockets.delete(this.destroyedRockets.keys().next().value!);
      }
      this.effects.emit(event, at);
      this.audio.emit(event);
    }
  }

  update(serverNow: number, quality: AtmosphereQuality = "medium", weather: { precipitation: number; snowCover: number } = { precipitation: 0, snowCover: 0 }): void {
    if (!Number.isFinite(serverNow)) return;
    const started = performance.now(), dt = Math.min(.12, Math.max(0, (serverNow - this.updatedAt) / 1000));
    this.now = serverNow; this.updatedAt = serverNow; this.quality = quality; this.shadowDrawCalls = 0;
    for (const actor of this.actors.values()) {
      const previousSpeed = actor.pose.speed;
      interpolateCombatActor(actor.previous, actor.current, actor.previousAt, actor.currentAt, serverNow, actor.pose);
      const pose = actor.pose, disabled = actor.current[13] === 5;
      pose.phase += (actor.current[2] === 2 ? actor.current[16] / .45 : pose.speed * (actor.current[2] === 0 ? 4.3 : 1.5)) * dt;
      pose.collapse += ((disabled ? 1 : 0) - pose.collapse) * (1 - Math.exp(-dt * 9));
      pose.recoil = Math.max(0, 1 - (serverNow - actor.recoilAt) / 170);
      pose.damaged = 1 - actor.current[12];
      pose.state = actor.current[13]; pose.damageStage = actor.current[17]; pose.damageZone = actor.current[18]; pose.damageSeed = actor.current[19];
      pose.cannonRecoil = Math.max(0, 1 - (serverNow - actor.cannonAt) / 650);
      pose.hitResponse = Math.max(0, 1 - (serverNow - actor.hitAt) / 380);
      const acceleration = dt > 0 ? (pose.speed - previousSpeed) / dt : 0;
      const pitchTarget = Math.max(-.032, Math.min(.032, acceleration * (actor.current[2] === 2 ? .012 : .003)));
      pose.hullPitch = (pose.hullPitch ?? 0) + (pitchTarget - (pose.hullPitch ?? 0)) * (1 - Math.exp(-dt * 5));
      pose.hullRoll = actor.current[2] === 2 ? -(pose.steering ?? 0) * Math.min(.045, pose.speed * .006) : 0;
      actor.parts.build(actor.current[2], pose, this.heightAt, serverNow);
      if (actor.current[2] === 2) {
        this.crew.update(actor.current[0], actor.current[1], pose, serverNow);
        if (!disabled && pose.speed > .45) for (const [index, foot] of actor.parts.feet.entries())
          this.effects.addWheelDust({ x: foot.x, y: foot.ground + .07, z: foot.z }, pose.speed, weather, serverNow, actor.current[0] * 8 + index);
      } else if (actor.current[2] === 1 && !disabled && pose.speed > .12) {
        for (const side of [-1, 1]) this.effects.addWheelDust({ x: pose.x + Math.cos(pose.yaw) * side * 1.65 - Math.sin(pose.yaw) * 2.7,
          y: pose.y + .12, z: pose.z - Math.sin(pose.yaw) * side * 1.65 - Math.cos(pose.yaw) * 2.7 }, pose.speed * 1.45, weather, serverNow, actor.current[0] * 8 + (side < 0 ? 0 : 1));
      }
      if (disabled || actor.current[2] === 2 && pose.damaged > .5) {
        const forward = actor.current[2] === 2 ? 1.7 : -.5;
        this.effects.addDisabledSmoke({ x: pose.x + Math.sin(pose.yaw) * forward, y: pose.y + (actor.current[2] === 0 ? .8 : 1.25),
          z: pose.z + Math.cos(pose.yaw) * forward }, actor.current[2], serverNow, actor.current[0], disabled ? 1 : pose.damaged);
      }
    }
    const effectsStarted = performance.now(); this.effects.update(serverNow, quality);
    this.effectsUpdateCpuMs = performance.now() - effectsStarted;
    this.rocketVisuals.length = 0; this.rocketLights.length = 0;
    for (const [id, rocket] of this.rockets) {
      interpolateCombatRocket(rocket.previous, rocket.current, rocket.previousAt, rocket.currentAt, serverNow, rocket.pose);
      this.rocketVisuals.push(rocket.pose);
      if (serverNow - rocket.trailAt >= (quality === "low" ? 120 : 85)) {
        this.effects.trail(id, rocket.pose, serverNow); rocket.trailAt = serverNow;
      }
      if (quality !== "low" && this.rocketLights.length < 2) this.rocketLights.push({ id: `combat-rocket-${id}`,
        position: [rocket.pose.x, rocket.pose.y, rocket.pose.z], color: [1, .32, .07], radius: 2.5, intensity: .7 });
    }
    this.animationCpuMs = performance.now() - started;
    this.peakLights = Math.max(this.peakLights, this.effects.activeLights + this.rocketLights.length);
  }

  render(camera: Camera): void {
    this.audio.listener(camera);
    this.actorDrawCalls = 0; this.visibleCount = 0;
    this.drawActors(camera, false);
    this.renderer.metrics.visibleAiObjects += this.visibleCount;
  }
  renderShadow(camera: Camera): void { this.drawActors(camera, true); }
  renderEffects(camera: Camera): void { const started = performance.now(); this.effectRenderer.render(camera, this.effects, this.rocketVisuals, this.now); this.vfxRenderCpuMs = performance.now() - started; }
  appendLocalLights(lights: LocalLight[]): void { this.effects.appendLocalLights(lights); lights.push(...this.rocketLights); }
  screenFlash(eye: readonly number[]): number { return this.effects.screenFlash(eye); }
  cameraImpulse(eye: readonly number[], output: [number, number, number]): void { this.effects.cameraImpulse(eye, output); }
  addContactShadows(shadows: ActorContactShadows): void {
    for (const actor of this.actors.values()) shadows.addActor(actor.pose.x, actor.pose.y, actor.pose.z, combatActorGeometry(actor.current[2]).shadowRadius, 3);
  }

  snapshot(camera?: Camera, canvas?: HTMLCanvasElement) {
    return { epoch: this.epoch, tick: this.tick, at: this.at, enabled: this.enabled, phase: this.phase, theater: COMBAT_THEATER,
      count: this.actors.size, actorCount: this.actors.size, rocketCount: this.rockets.size, visibleCount: this.visibleCount, visibleActorCount: this.visibleCount,
      visibleRockets: this.effectRenderer.visibleRockets, actorDrawCalls: this.actorDrawCalls, shadowDrawCalls: this.shadowDrawCalls,
      effectsDrawCalls: this.effectRenderer.drawCalls, vfxDrawCalls: this.effectRenderer.drawCalls, drawCalls: this.actorDrawCalls + this.effectRenderer.drawCalls,
      animationCpuMs: this.animationCpuMs, effectsUpdateCpuMs: this.effectsUpdateCpuMs, vfxRenderCpuMs: this.vfxRenderCpuMs, quality: this.quality, limits: { ...COMBAT_LIMITS, ...COMBAT_VFX_BUDGET.quality[this.quality] },
      crew: this.crew.snapshot(),
      debris: this.effects.activeDebris, smoke: this.effects.activeSmoke, dust: this.effects.activeDust, shockwaves: this.effects.activeShockwaves,
      peakDebris: this.effects.peakDebris, peakSmoke: this.effects.peakSmoke, peakDust: this.effects.peakDust, peakShockwaves: this.effects.peakShockwaves,
      particles: this.effects.activeParticles, trails: this.effects.activeTrails, beams: this.effects.activeBeams,
      transientLights: this.effects.activeLights + this.rocketLights.length, peakLights: this.peakLights,
      visibleParticles: this.effectRenderer.visibleParticles, peakParticles: this.effects.peakParticles, peakEffects: this.effects.peakParticles, peakTrails: this.effects.peakTrails,
      receivedEvents: this.effects.events, eventCounts: { ...this.effects.eventCounts }, duplicateEvents: this.duplicateEvents, droppedEvents: this.droppedEvents,
      recentEventCount: this.recentEvents.size,
      audio: this.audio.snapshot(),
      actors: [...this.actors.values()].map(actor => ({ id: actor.current[0], epoch: actor.current[1], type: actor.current[2], faction: actor.current[3],
        geometry: combatActorGeometry(actor.current[2]), scanner: actor.current[2] === 0 ? houndSensorPresentation(actor.pose, this.now) : null,
        crew: actor.current[2] === 2 ? this.crew.snapshot(actor.current[0]).members : [],
        damageStage: actor.current[17], damageZone: actor.current[18], damageSeed: actor.current[19], steering: actor.current[15], vehicleSpeed: actor.current[16],
        integrity: actor.current[12], state: actor.current[13], weaponSequence: actor.current[14], current: actor.current,
        renderPosition: { ...actor.pose }, visible: actor.visible, feet: actor.parts.feet.map(foot => ({ x: foot.x, y: foot.y, z: foot.z, ground: foot.ground })),
        parts: actor.parts.metalCount + actor.parts.glowCount + actor.parts.wheelCount, legSegmentCount: actor.current[2] === 0 ? 8 : 0,
        screen: camera && canvas ? project(actor.pose.x, actor.pose.y + combatActorGeometry(actor.current[2]).centerY, actor.pose.z, camera, canvas) : null })),
      rockets: [...this.rockets.values()].map(rocket => ({ id: rocket.current[0], epoch: rocket.current[1], current: rocket.current, renderPosition: { ...rocket.pose } })),
    };
  }

  reset(): void {
    this.actors.clear(); this.rockets.clear(); this.recentEvents.clear(); this.eventOrder.length = 0; this.destroyedRockets.clear();
    this.rocketVisuals.length = 0; this.rocketLights.length = 0; this.effects.reset(); this.audio.reset(); this.crew.reset();
    this.epoch = -1; this.tick = -1; this.at = 0; this.now = 0; this.updatedAt = 0; this.enabled = false;
    this.peakLights = 0; this.effectsUpdateCpuMs = 0; this.vfxRenderCpuMs = 0;
    this.maximumEventId = -1; this.duplicateEvents = 0; this.droppedEvents = 0; this.actorDrawCalls = 0; this.shadowDrawCalls = 0; this.visibleCount = 0;
  }
  dispose(): void { this.reset(); this.audio.dispose(); this.crew.dispose(); this.effectRenderer.dispose(); this.renderer.destroyMesh(this.box); this.renderer.destroyMesh(this.wheel); }

  private drawActors(camera: Camera, shadow: boolean): void {
    let metals = 0, glows = 0, wheels = 0;
    for (const actor of this.actors.values()) {
      const p = actor.pose, geometry = combatActorGeometry(actor.current[2]);
      const visible = camera.isSphereVisible(p.x, p.y + geometry.centerY, p.z, geometry.cullRadius);
      if (!shadow) actor.visible = visible;
      if (!visible) continue;
      if (!shadow) this.visibleCount++;
      const parts = actor.parts;
      this.metalData.set(parts.metal.subarray(0, parts.metalCount * 20), metals * 20); metals += parts.metalCount;
      this.glowData.set(parts.glow.subarray(0, parts.glowCount * 20), glows * 20); glows += parts.glowCount;
      this.wheelData.set(parts.wheels.subarray(0, parts.wheelCount * 20), wheels * 20); wheels += parts.wheelCount;
    }
    const before = shadow ? this.renderer.metrics.shadowDrawCalls : this.renderer.metrics.drawCalls;
    if (metals) this.renderer.drawStaticInstanced(this.box, this.metalData, metals, METAL, "ai");
    if (glows) this.renderer.drawStaticInstanced(this.box, this.glowData, glows, GLOW, "ai");
    if (wheels) this.renderer.drawStaticInstanced(this.wheel, this.wheelData, wheels, METAL, "ai");
    this.crew.render(camera, shadow);
    const draws = (shadow ? this.renderer.metrics.shadowDrawCalls : this.renderer.metrics.drawCalls) - before;
    if (shadow) this.shadowDrawCalls += draws; else this.actorDrawCalls += draws;
  }
}

export function interpolateCombatActor(previous: PackedCombatActor, current: PackedCombatActor, previousAt: number, currentAt: number,
  now: number, out: MachinePose): void {
  const discontinuity = previous[1] !== current[1];
  const renderAt = now - COMBAT_INTERPOLATION_DELAY_MS, interval = currentAt - previousAt;
  const alpha = discontinuity || interval <= 0 ? 1 : Math.max(0, Math.min(1, (renderAt - previousAt) / interval));
  const extra = current[13] === 5 || discontinuity ? 0 : Math.min(COMBAT_EXTRAPOLATION_LIMIT_MS, Math.max(0, renderAt - currentAt)) / 1000;
  out.x = previous[4] + (current[4] - previous[4]) * alpha + current[10] * extra;
  out.y = previous[5] + (current[5] - previous[5]) * alpha;
  out.z = previous[6] + (current[6] - previous[6]) * alpha + current[11] * extra;
  out.yaw = previous[7] + angleDelta(previous[7], current[7]) * alpha;
  out.turretYaw = previous[8] + angleDelta(previous[8], current[8]) * alpha;
  out.aimPitch = previous[9] + (current[9] - previous[9]) * alpha;
  out.speed = current[13] === 5 ? 0 : Math.hypot(current[10], current[11]);
  out.steering = previous[15] + (current[15] - previous[15]) * alpha;
}
export function interpolateCombatRocket(previous: PackedCombatRocket, current: PackedCombatRocket, previousAt: number, currentAt: number,
  now: number, out: RocketVisual): void {
  const renderAt = now - COMBAT_INTERPOLATION_DELAY_MS, interval = currentAt - previousAt;
  const same = previous[1] === current[1], alpha = same && interval > 0 ? Math.max(0, Math.min(1, (renderAt - previousAt) / interval)) : 1;
  const extra = same ? Math.min(COMBAT_EXTRAPOLATION_LIMIT_MS, Math.max(0, renderAt - currentAt)) / 1000 : 0;
  out.x = previous[4] + (current[4] - previous[4]) * alpha + current[7] * extra;
  out.y = previous[5] + (current[5] - previous[5]) * alpha + current[8] * extra;
  out.z = previous[6] + (current[6] - previous[6]) * alpha + current[9] * extra;
  out.vx = current[7]; out.vy = current[8]; out.vz = current[9];
}
const angleDelta = (a: number, b: number): number => Math.atan2(Math.sin(b - a), Math.cos(b - a));
function project(x: number, y: number, z: number, camera: Camera, canvas: HTMLCanvasElement) {
  const m = camera.viewProjection, bounds = canvas.getBoundingClientRect(), w = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!;
  if (w <= 0) return null;
  const cx = (m[0]! * x + m[4]! * y + m[8]! * z + m[12]!) / w, cy = (m[1]! * x + m[5]! * y + m[9]! * z + m[13]!) / w;
  return { x: bounds.left + (cx + 1) * bounds.width / 2, y: bounds.top + (1 - cy) * bounds.height / 2, visible: Math.abs(cx) <= 1 && Math.abs(cy) <= 1 };
}
